import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { push } from 'connected-react-router';
import Dialog from 'src/components/common/Dialog';
import useFetch from 'use-http';
import PersonnelToast from '../../components/PersonnelToast';
import EmployeeBenefitYearFlag from 'src/models/personnel/EmployeeBenefitYearFlag';
import Button from 'src/components/controls/forms/redesign/FormButton';

import '../../BenefitStyleSheet.scss';

interface IYearMatrixDialogProps {
    dialogName: string;
    hideDialog: () => void;
    showToast: (message: string, color?: string, persistent?: boolean) => void;
}

// How many year columns are visible at once - the grid pages through the full year range this
// many columns at a time rather than rendering every year from a hardcoded start to a hardcoded
// end (the old design showed Y2013-Y2036 unconditionally, including a decade of years with no
// possible data yet).
const YEARS_PER_PAGE = 12;

const YearMatrixDialog = (props: IYearMatrixDialogProps) => {
    const { get, response } = useFetch();
    const dispatch = useDispatch();
    const [toast, setToast] = useState<{ message: string; color?: string, persistent?: boolean; } | null>(null);
    const [yearFlags, setYearFlags] = useState<Array<EmployeeBenefitYearFlag>>();
    const [windowStart, setWindowStart] = useState<number>(undefined);

    useEffect(() => {
        onCreate();
    }, []);

    const showToast = (message: string, color?: string, persistent?: boolean) => {
        setToast({ message, persistent, color });
    };

    const onCreate = () => {
        getEmployeeBenefitYearFlags();
    };

    const getEmployeeBenefitYearFlags = async () => {
        let results = await get(`v1.0/landmen/benefits/get-benefit-matrix`);

        if (response.ok && results) {
            setYearFlags(results);

            const years = (results as Array<EmployeeBenefitYearFlag>)
                .map((r) => r.year)
                .filter((y): y is number => y !== null && y !== undefined);
            const latestYear = years.length > 0 ? Math.max(...years) : new Date().getFullYear();

            // Anchor the initial window at the latest year with data, showing YEARS_PER_PAGE
            // years back from there - per explicit direction, rather than a hardcoded range.
            setWindowStart(Math.max(getMinYear(years), latestYear - (YEARS_PER_PAGE - 1)));
        } else {
            showToast('Unable to get benefit matrix info');
        }
    };

    const getMinYear = (years: Array<number>) => {
        return years.length > 0 ? Math.min(...years) : new Date().getFullYear();
    };

    // distinct employees, in the order the backend already returned them (TSD_NO ascending)
    const employees = (() => {
        const seen = new Set<number>();
        const result: Array<{ tsdNo: number; akaName: string }> = [];
        yearFlags?.forEach((flag) => {
            if (!seen.has(flag.tsdNo)) {
                seen.add(flag.tsdNo);
                result.push({ tsdNo: flag.tsdNo, akaName: flag.akaName });
            }
        });
        return result;
    })();

    const definedYears = (yearFlags ?? [])
        .map((f) => f.year)
        .filter((y): y is number => y !== null && y !== undefined);
    const minYear = getMinYear(definedYears);
    const maxYear = definedYears.length > 0 ? Math.max(...definedYears) : new Date().getFullYear();

    const definedKeys = new Set(
        (yearFlags ?? [])
            .filter((f) => f.year !== null && f.year !== undefined)
            .map((f) => `${f.tsdNo}-${f.year}`)
    );

    const hasBenefitYear = (tsdNo: number, year: number) => {
        return definedKeys.has(`${tsdNo}-${year}`);
    };

    const visibleYears = windowStart === undefined
        ? []
        : Array.from(
            { length: Math.min(YEARS_PER_PAGE, maxYear - windowStart + 1) },
            (_, i) => windowStart + i
        );

    // matches the year-navigation arrows on EmployeeBenefitsPage.tsx: double-arrows jump to the
    // oldest/latest possible window, single arrows step one year at a time.
    const moveToOldestWindow = () => {
        setWindowStart(minYear);
    };

    const stepBack = () => {
        if (windowStart > minYear) {
            setWindowStart(windowStart - 1);
        }
    };

    const stepForward = () => {
        if (windowStart + YEARS_PER_PAGE - 1 < maxYear) {
            setWindowStart(windowStart + 1);
        }
    };

    const moveToLatestWindow = () => {
        setWindowStart(Math.max(minYear, maxYear - (YEARS_PER_PAGE - 1)));
    };

    // Mirrors Oracle's WHEN-MOUSE-DOUBLECLICK [oid 1333]: sets the clicked row's employee as the
    // current employee, closes this window, and reloads the main Benefits screen for them
    // (Execute_Trigger('populate_lm')). Unlike Oracle's own Matrix trigger (which only carries
    // TSD_NO, landing on whatever year Populate_LM defaults to), this carries the clicked cell's
    // year too - matching the more complete pattern Oracle's own Insurance/Benefits Dashboard
    // double-click triggers already use (oid 1470/1868: TSD_NO + YEAR, stepping LM_BENEFIT
    // records until landing on that exact year) - per explicit direction, since the Matrix's own
    // TSD_NO-only version was the outlier, not a better Oracle match.
    //
    // Doesn't fetch/dispatch the employee itself - a first attempt did, racing this
    // (about-to-unmount) dialog's dispatch against EmployeeBenefitsPage's own mount-time effects,
    // which sometimes read the still-stale Redux selectedPersonnel from before this navigation.
    // Instead this follows the same pattern ClientProjectSearch.tsx already uses for
    // client/project selection: drop the target off in localStorage and let the page's own
    // PersonnelSearch component (already mounted on every personnel page, single source of truth
    // for "which employee is selected") pick it up on its next mount and select it through its
    // normal flow.
    const drillIntoEmployee = (tsdNo: number, year: number) => {
        localStorage.setItem('personnelPendingTsdNo', tsdNo.toString());
        localStorage.setItem('personnelPendingYear', year.toString());
        props.hideDialog();
        dispatch(push('/personnel/employee-benefits'));
    };

    return (
        <Dialog title="Employee Benefit Years" dialogName={props.dialogName} bodyStyle={{ width: '75%', height: '75%' }}>
            <div>
                {toast && (
                    <PersonnelToast
                        message={toast.message}
                        color={toast.color}
                        onClose={() => setToast(null)}
                        persistent={toast.persistent}
                    />
                )}
            </div>
            <div>
                <div style={{ display: 'flex', alignItems: 'center', paddingBottom: '0.5rem' }}>
                    <a onClick={() => moveToOldestWindow()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Oldest years">
                        <i style={{ fontSize: '2.5rem' }} className="far fa-angle-double-left clickable" />
                    </a>
                    <a onClick={() => stepBack()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Back one year">
                        <i style={{ fontSize: '2.5rem' }} className="far fa-angle-left clickable" />
                    </a>
                    <a onClick={() => stepForward()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Forward one year">
                        <i style={{ fontSize: '2.5rem' }} className="far fa-angle-right clickable" />
                    </a>
                    <a onClick={() => moveToLatestWindow()} style={{ paddingLeft: '6px', paddingRight: '6px' }} title="Latest years">
                        <i style={{ fontSize: '2.5rem' }} className="far fa-angle-double-right clickable" />
                    </a>
                    {visibleYears.length > 0 &&
                        <span style={{ marginLeft: '1rem' }}>{visibleYears[0]} - {visibleYears[visibleYears.length - 1]}</span>
                    }
                </div>
                <div style={{ overflowY: 'scroll', maxHeight: '55rem' }}>
                    <table style={{ width: '100%' }}>
                        <thead>
                            <tr style={{ background: '#d6d6d6' }}>
                                <th style={{ width: '6%' }} className="sticky-header">Emp. No</th>
                                <th style={{ width: '25%' }} className="sticky-header">Name</th>
                                {visibleYears.map((year) => (
                                    <th key={year} style={{ width: '3%' }} className="sticky-header">{year}</th>
                                ))}
                            </tr>
                        </thead>
                        {employees.map((employee, index: number) => {
                            return (
                                <tbody key={index}>
                                    <tr className={`contract-pay-history-active-item-${index}`}>
                                        <td style={{ width: '6%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {employee.tsdNo}
                                        </td>
                                        <td style={{ width: '25%', border: '1px solid black', borderCollapse: 'collapse' }}>
                                            {employee.akaName}
                                        </td>
                                        {visibleYears.map((year) => (
                                            <td
                                                key={year}
                                                style={{ width: '3%', border: '1px solid black', borderCollapse: 'collapse', textAlign: 'center', cursor: 'pointer' }}
                                                title={`Double-click to open ${employee.akaName}'s ${year} Benefits page`}
                                                onDoubleClick={() => drillIntoEmployee(employee.tsdNo, year)}
                                            >
                                                {hasBenefitYear(employee.tsdNo, year) ? 'X' : ''}
                                            </td>
                                        ))}
                                    </tr>
                                </tbody>
                            );
                        })}
                    </table>
                </div>
                <div className="button-row" style={{ paddingBottom: '1rem' }}>
                    <Button text="Close" style={{ width: '25%' }} onClick={() => props.hideDialog()} intent="primary" />
                </div>
            </div>
        </Dialog>
    );
};

export default YearMatrixDialog;



// WEBPACK FOOTER //
// ./src/components/personnel/dialogs/benefits/YearMatrixDialog.tsx